
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import type { RentalStatus, DbRentalInsert, RentalCreate } from '@/types/entities/rental';
import { rentalCreateToDbInsert } from '@/utils/mappers/rentalMappers';
import { useAuth } from '@/context/AuthContext';
import { useTaskAutoCreation, type RentalTaskData as AutoRentalTaskData } from '@/services/tasks/taskAutoCreationService';

export const useRentalCreation = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { createTasksForRental } = useTaskAutoCreation();

  // Minimal input shape used by this hook (non-exhaustive)
  type InventoryItemInput = { id: string; name?: string; quantity?: number };
  type KitInput = { id: string; quantity?: number };
  type RentalInput = {
    clientId?: string | null;
    clientName?: string | null;
    inventoryItems?: InventoryItemInput[] | null;
    selectedKits?: KitInput[] | null;
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    status?: string | null;
    totalAmount?: number | null;
    notes?: string | null;
    storeItems?: unknown;
    signatureId?: string | null;
    isStorePurchase?: boolean;
    rentalPointId?: string;
    returnPointId?: string | null;
    tariffId?: string | null;
    paymentMethodId?: string | null;
    paymentStatus?: string | null;
    discount?: number | null;
    depositAmount?: number | null;
  };

  const createRental = async (rentalData: RentalInput) => {
    setLoading(true);
    try {
      if (!user) throw new Error('User not authenticated');

      // Валидация: требуется хотя бы один источник — элементы инвентаря ИЛИ комплекты
      const hasInventoryItems = Array.isArray(rentalData.inventoryItems) && rentalData.inventoryItems.length > 0;
      const hasKits = Array.isArray(rentalData.selectedKits) && rentalData.selectedKits.length > 0;
      if (!hasInventoryItems && !hasKits) {
        throw new Error('Необходимо выбрать хотя бы один элемент: инвентарь или комплект');
      }

      // Нормализуем статус аренды к допустимым значениям БД c поддержкой тегов и регистронезависимой маппинг
      const ALLOWED_RENTAL_STATUSES = ['pending', 'active', 'completed', 'cancelled', 'returned', 'overdue'] as const;
      const STATUS_MAPPING: Record<string, string> = {
        // Русские варианты (теги) — ключи в нижнем регистре
        'ожидание': 'pending',
        'к сборке': 'pending',
        'активно': 'active',
        'активный': 'active',
        'активная': 'active',
        'завершено': 'completed',
        'завершен': 'completed',
        'завершена': 'completed',
        'отменено': 'cancelled',
        'отменен': 'cancelled',
        'отменена': 'cancelled',
        'возвращено': 'returned',
        'к сдаче': 'returned',
        'просрочено': 'overdue',
        'просрочен': 'overdue',
        'просроченная': 'overdue',
        // English fallbacks
        'pending': 'pending',
        'active': 'active',
        'completed': 'completed',
        'cancelled': 'cancelled',
        'returned': 'returned',
        'overdue': 'overdue',
      };

      const rawStatus = String(rentalData.status ?? 'active');
      const normalized = rawStatus.trim().toLowerCase();
      const mappedStatus = STATUS_MAPPING[normalized] || normalized;
      const safeStatus = (ALLOWED_RENTAL_STATUSES as readonly string[]).includes(mappedStatus)
        ? (mappedStatus as RentalStatus)
        : ((): RentalStatus => {
            // Предупреждаем о подстановке статуса по умолчанию
            console.warn('[useRentalCreation] Invalid rental status provided, falling back to "active"', {
              provided: rawStatus,
              normalized,
              mapped: mappedStatus,
            });
            toast({
              title: 'Предупреждение',
              description: 'Выбранный статус аренды не поддерживается и будет заменён на Активно.',
              variant: 'default',
            });
            return 'active';
          })();

      // Подготавливаем данные для создания аренды в формате RentalCreate
      const rentalCreateData: RentalCreate = {
        userId: user.id,
        clientId: rentalData.clientId ?? null,
        // Если есть элементы инвентаря — берем первый; иначе допускаем null (для аренды только с комплектом)
        inventoryItemId: hasInventoryItems ? rentalData.inventoryItems[0].id : null,
        startDate: rentalData.startDate ? new Date(rentalData.startDate) : new Date(),
        endDate: rentalData.endDate ? new Date(rentalData.endDate) : new Date(Date.now() + 24*60*60*1000),
        status: safeStatus,
        totalAmount: typeof rentalData.totalAmount === 'number' ? rentalData.totalAmount : 0,
        notes: rentalData.notes ?? null,
        // Требуемые полня из RentalCreate согласно типам
        inventoryItems: rentalData.inventoryItems ?? null,
        storeItems: rentalData.storeItems ?? null,
        signatureId: rentalData.signatureId ?? null,
        isStorePurchase: Boolean(rentalData.isStorePurchase),
        // Archive fields defaults
        archivedAt: null,
        archivedBy: null,
        // Extended fields - передаем все поля, маппер сам обработает пустые строки
        rentalPointId: rentalData.rentalPointId,
        returnPointId: rentalData.returnPointId,
        tariffId: rentalData.tariffId,
        // Убираем дублирование paymentMethod и paymentMethodId - используем только paymentMethodId
        paymentMethodId: rentalData.paymentMethodId,
        // Не навязываем значение по умолчанию — пусть БД применит DEFAULT 'pending'.
        // Также это позволит мапперу пропустить UUID и не записывать его в текстовое поле.
        paymentStatus: rentalData.paymentStatus,
        discount: rentalData.discount,
        depositAmount: rentalData.depositAmount,
      };

      // Используем маппер для корректного преобразования данных
      const dbRentalData = rentalCreateToDbInsert(rentalCreateData);

      console.log('[useRentalCreation] Creating rental with DB data:', dbRentalData);

      // Используем правильную таблицу rentals для аренды
      // Приведение типов для обхода ограничений автогенерированных типов Supabase
      const { data: rental, error } = await supabaseWorker
        .from('rentals')
        .insert(dbRentalData as DbRentalInsert)
        .select()
        .single();

      if (error) {
        // Улучшаем диагностику ошибок Supabase/PostgREST
        const enriched = {
          message: (error && typeof error === 'object' && 'message' in error) ? (error as { message?: unknown }).message : undefined,
          code: (error && typeof error === 'object' && 'code' in error) ? (error as { code?: unknown }).code : undefined,
          details: (error && typeof error === 'object' && 'details' in error) ? (error as { details?: unknown }).details : undefined,
          hint: (error && typeof error === 'object' && 'hint' in error) ? (error as { hint?: unknown }).hint : undefined,
        };
        console.error('[useRentalCreation] Database error:', enriched, 'payload:', dbRentalData);
        // Пробрасываем более понятное сообщение наверх (и в тост)
        const parts = [enriched.message, enriched.code, enriched.details]
          .map((v) => (typeof v === 'string' ? v : undefined))
          .filter(Boolean) as string[];
        const display = parts.join(' • ');
        throw new Error(display || 'Не удалось создать аренду');
      }

      console.log('[useRentalCreation] Rental created successfully:', rental);

      // Автоматическое создание задач для аренды (если включено)
      try {
        if (user) {
          const taskSettings = {
            autoCreateTasksForOrders: true,
            autoCreateTasksForRentals: true,
            defaultTaskCategory: 'rental',
            defaultTaskStatus: 'pending',
          } as const;

          const rentalId = (rental && typeof rental === 'object' && 'id' in rental && typeof (rental as { id?: unknown }).id === 'string')
            ? (rental as { id: string }).id
            : '';

          const taskPayload: AutoRentalTaskData = {
            rentalId,
            clientId: rentalData.clientId ?? null,
            clientName: (typeof rentalData.clientName === 'string' && rentalData.clientName) ? rentalData.clientName : 'Клиент',
            inventoryItems: Array.isArray(rentalData.inventoryItems)
              ? rentalData.inventoryItems.map((i) => ({ name: i.name ?? 'Позиция', quantity: i.quantity ?? 1 }))
              : [],
            startDate: rentalCreateData.startDate,
            endDate: rentalCreateData.endDate,
          };

          await createTasksForRental(taskPayload, taskSettings);
          console.log('[useRentalCreation] Rental tasks created automatically');
        }
      } catch (e) {
        console.warn('[useRentalCreation] Failed to auto-create rental tasks', e);
        // Не прерываем основной поток
      }

      // Логируем успешное создание аренды
      // Примечание: таблица rentals имеет простую структуру и не поддерживает
      // Обновлено после миграции схемы - сложные поля как inventory или rental_kits в JSON формате
      // Если нужна поддержка множественных элементов инвентаря или комплектов,
      // следует создать отдельные связующие таблицы или использовать orders
      if (rentalData.inventoryItems && rentalData.inventoryItems.length > 1) {
        console.warn('[useRentalCreation] Multiple inventory items detected, but rentals table supports only one inventory_id');
        toast({
          title: "Предупреждение",
          description: "Аренда создана с одним элементом инвентаря. Для множественных элементов используйте заказы.",
          variant: "default",
        });
      }

      if (rentalData.selectedKits && rentalData.selectedKits.length > 0) {
        console.warn('[useRentalCreation] Rental kits detected, but rentals table does not support kits');
        toast({
          title: "Предупреждение",
          description: "Аренда создана без комплектов. Для комплектов используйте заказы.",
          variant: "default",
        });
      }

      return rental;
    } catch (error: unknown) {
      console.error('Error creating rental:', error);
      const message = (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string')
        ? (error as { message: string }).message
        : 'Не удалось создать аренду';
      toast({
        title: "Ошибка",
        description: message,
        variant: "destructive",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createRental,
    loading
  };
};
